import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type AppToastVariant = 'success' | 'danger' | 'warning' | 'info';

export interface AppToastState {
  open: boolean;
  variant: AppToastVariant;
  label: string;
  description?: string;
}

export interface AppToastShowOptions {
  /** Legacy B3ToastService status mapping */
  status?: 'success' | 'warning' | 'info' | 'error' | 'danger';
  text?: string;
  label?: string;
  description?: string;
  variant?: AppToastVariant;
}

/**
 * Global toast host driven by new DS web components (b3-toast-region / b3-toast).
 * Replaces @b3/design-system-lib B3ToastService.
 */
@Injectable({ providedIn: 'root' })
export class AppToastService {
  private readonly toastSubject = new BehaviorSubject<AppToastState | null>(null);
  readonly toast$ = this.toastSubject.asObservable();

  constructor(private readonly ngZone: NgZone) {}

  show(options: AppToastShowOptions): void {
    const variant = options.variant ?? this.mapStatus(options.status);
    const label = (options.label ?? options.text ?? '').trim() || 'Notificação';

    // b3-click / web components podem emitir fora da NgZone — força CD no host.
    this.ngZone.run(() => {
      this.toastSubject.next({
        open: true,
        variant,
        label,
        description: options.description,
      });
    });
  }

  dismiss(): void {
    this.ngZone.run(() => {
      this.toastSubject.next(null);
    });
  }

  private mapStatus(status?: AppToastShowOptions['status']): AppToastVariant {
    switch (status) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      case 'error':
      case 'danger':
        return 'danger';
      default:
        return 'info';
    }
  }
}
